import { SkillAssessment, CurriculumAnalytics } from "../types/curriculum";
import { EducationData } from "../types/education";

export interface SkillMetrics {
  skillId: string;
  skillName: string;
  currentLevel: number;
  targetLevel: number;
  progress: number; // 0-100 percentage towards next level
  strengths: string[];
  weaknesses: string[];
  lastAssessed: string;
  assessments: SkillAssessment[];
  trend: "improving" | "stable" | "declining";
  nextMilestone: {
    level: number;
    requirements: string[];
    estimatedTime: string;
  };
}

export interface LearningInsight {
  type: "strength" | "weakness" | "recommendation" | "achievement";
  title: string;
  description: string;
  priority: "high" | "medium" | "low";
  actionable: boolean;
  relatedTopics: string[];
  relatedLessons: string[];
  timestamp: string;
}

export interface ProgressAnalytics {
  overview: {
    totalLessons: number;
    completedLessons: number;
    totalTopics: number;
    completedTopics: number;
    totalPaths: number;
    completedPaths: number;
    overallProgress: number;
    averageQuizScore: number;
    totalTimeSpent: number;
    streakDays: number;
    lastActivityDate: string;
  };
  skills: SkillMetrics[];
  insights: LearningInsight[];
  trends: {
    weeklyProgress: Array<{
      week: string;
      lessonsCompleted: number;
      timeSpent: number;
      quizScore: number;
    }>;
    skillProgression: Array<{
      skillId: string;
      date: string;
      level: number;
    }>;
  };
  recommendations: {
    nextTopics: string[];
    recommendedPaths: string[];
    skillFocus: string[];
    timeAllocation: Record<string, number>;
  };
}

export class SkillAssessmentService {
  private skillLevelCriteria = {
    1: {
      name: "Novice",
      description: "Basic awareness",
      requirements: ["Can identify basic concepts"],
    },
    2: {
      name: "Beginner",
      description: "Working knowledge",
      requirements: ["Can apply basic concepts", "Understands terminology"],
    },
    3: {
      name: "Intermediate",
      description: "Competent application",
      requirements: ["Can work independently", "Handles complex scenarios"],
    },
    4: {
      name: "Advanced",
      description: "Expert proficiency",
      requirements: ["Can teach others", "Innovates solutions"],
    },
    5: {
      name: "Master",
      description: "Industry expertise",
      requirements: ["Recognized authority", "Creates new knowledge"],
    },
  };

  // Calculate skill level based on completed topics and quiz performance
  assessSkillLevel(
    skillId: string,
    educationData: EducationData,
    curriculumService: any,
  ): SkillMetrics {
    const relatedTopics = curriculumService
      .getAllTopics()
      .filter((topic: any) => topic.skills.includes(skillId));

    if (relatedTopics.length === 0) {
      return this.createEmptySkillMetrics(skillId);
    }

    const completedTopics = relatedTopics.filter((topic: any) =>
      curriculumService.isTopicCompleted(topic.id, educationData.progress),
    );

    const topicProgress = relatedTopics.map((topic: any) => ({
      id: topic.id,
      progress: curriculumService.getTopicProgress(
        topic.id,
        educationData.progress,
      ),
      difficulty: topic.difficulty,
    }));

    // Calculate base level from completion
    const completionRatio = completedTopics.length / relatedTopics.length;
    let baseLevel = Math.floor(completionRatio * 5) + 1;
    baseLevel = Math.min(5, Math.max(1, baseLevel));

    // Adjust based on quiz performance
    const quizScores = Object.values(educationData.progress)
      .flatMap((progress) => progress.quizScores || [])
      .filter((score) => score > 0);

    const averageQuizScore =
      quizScores.length > 0
        ? quizScores.reduce((sum, score) => sum + score, 0) / quizScores.length
        : 0;

    // Boost level based on quiz performance
    if (averageQuizScore >= 90 && baseLevel < 5) baseLevel++;
    else if (averageQuizScore >= 80 && baseLevel < 4) baseLevel++;
    else if (averageQuizScore >= 70 && baseLevel < 3) baseLevel++;

    // Calculate progress towards next level
    const progressToNext = this.calculateProgressToNextLevel(
      completionRatio,
      averageQuizScore,
      baseLevel,
    );

    // Generate strengths and weaknesses
    const { strengths, weaknesses } = this.analyzeSkillPerformance(
      topicProgress,
      quizScores,
      relatedTopics,
    );

    // Calculate trend based on recent activity
    const trend = this.calculateSkillTrend(educationData, skillId);

    return {
      skillId,
      skillName: this.formatSkillName(skillId),
      currentLevel: baseLevel,
      targetLevel: Math.min(5, baseLevel + 1),
      progress: progressToNext,
      strengths,
      weaknesses,
      lastAssessed: new Date().toISOString(),
      assessments: this.generateSkillAssessments(skillId, educationData),
      trend,
      nextMilestone: this.getNextMilestone(baseLevel),
    };
  }

  // Generate comprehensive progress analytics
  generateProgressAnalytics(
    educationData: EducationData,
    curriculumService: any,
  ): ProgressAnalytics {
    const overview = this.calculateOverview(educationData, curriculumService);
    const skills = this.getAllSkillMetrics(educationData, curriculumService);
    const insights = this.generateLearningInsights(
      educationData,
      curriculumService,
      skills,
    );
    const trends = this.calculateTrends(educationData);
    const recommendations = this.generateRecommendations(
      educationData,
      curriculumService,
      skills,
    );

    return {
      overview,
      skills,
      insights,
      trends,
      recommendations,
    };
  }

  // Calculate mastery score for a specific skill
  calculateMasteryScore(
    skillId: string,
    educationData: EducationData,
    curriculumService: any,
  ): number {
    const skillMetrics = this.assessSkillLevel(
      skillId,
      educationData,
      curriculumService,
    );

    // Weight factors for mastery calculation
    const levelWeight = 0.4; // Current skill level
    const progressWeight = 0.3; // Progress towards next level
    const consistencyWeight = 0.2; // Consistency in performance
    const breadthWeight = 0.1; // Breadth of knowledge

    const levelScore = (skillMetrics.currentLevel / 5) * 100;
    const progressScore = skillMetrics.progress;
    const consistencyScore = this.calculateConsistencyScore(educationData);
    const breadthScore = this.calculateBreadthScore(
      skillId,
      educationData,
      curriculumService,
    );

    return Math.round(
      levelWeight * levelScore +
        progressWeight * progressScore +
        consistencyWeight * consistencyScore +
        breadthWeight * breadthScore,
    );
  }

  // Generate personalized learning recommendations
  generateLearningRecommendations(
    educationData: EducationData,
    curriculumService: any,
  ): LearningInsight[] {
    const insights: LearningInsight[] = [];
    const skills = this.getAllSkillMetrics(educationData, curriculumService);

    // Identify weak areas
    const weakSkills = skills.filter(
      (skill) => skill.trend === "declining" || skill.progress < 50,
    );
    weakSkills.forEach((skill) => {
      insights.push({
        type: "weakness",
        title: `Strengthen ${skill.skillName}`,
        description: `Your ${skill.skillName} skills need attention. Focus on ${skill.weaknesses.join(", ")}.`,
        priority: skill.currentLevel <= 2 ? "high" : "medium",
        actionable: true,
        relatedTopics: curriculumService
          .getTopicsByLevel(0)
          .map((t: any) => t.id),
        relatedLessons: curriculumService
          .getAllTopics()
          .filter((topic: any) => topic.skills.includes(skill.skillId))
          .flatMap((topic: any) =>
            curriculumService.getLessonsForTopic(topic.id),
          )
          .map((lesson: any) => lesson.id),
        timestamp: new Date().toISOString(),
      });
    });

    // Identify strengths to build upon
    const strongSkills = skills.filter(
      (skill) => skill.trend === "improving" && skill.progress > 70,
    );
    strongSkills.forEach((skill) => {
      insights.push({
        type: "strength",
        title: `Excel in ${skill.skillName}`,
        description: `You're doing great with ${skill.skillName}! Consider advanced topics to build on ${skill.strengths.join(", ")}.`,
        priority: "low",
        actionable: true,
        relatedTopics: curriculumService
          .getTopicsByLevel(1)
          .filter(
            (topic: any) =>
              topic.difficulty === "advanced" &&
              topic.skills.includes(skill.skillId),
          )
          .map((t: any) => t.id),
        relatedLessons: [],
        timestamp: new Date().toISOString(),
      });
    });

    // Generate achievement insights
    const completedPaths = curriculumService
      .getAllLearningPaths()
      .filter((path: any) =>
        curriculumService.isLearningPathCompleted(
          path.id,
          educationData.progress,
        ),
      );

    if (completedPaths.length > 0) {
      insights.push({
        type: "achievement",
        title: "Learning Path Completed!",
        description: `Congratulations! You've completed ${completedPaths.length} learning path(s).`,
        priority: "medium",
        actionable: false,
        relatedTopics: [],
        relatedLessons: [],
        timestamp: new Date().toISOString(),
      });
    }

    return insights;
  }

  // Private helper methods
  private createEmptySkillMetrics(skillId: string): SkillMetrics {
    return {
      skillId,
      skillName: this.formatSkillName(skillId),
      currentLevel: 1,
      targetLevel: 2,
      progress: 0,
      strengths: [],
      weaknesses: ["No completed topics yet"],
      lastAssessed: new Date().toISOString(),
      assessments: [],
      trend: "stable",
      nextMilestone: this.getNextMilestone(1),
    };
  }

  private formatSkillName(skillId: string): string {
    return skillId.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  }

  private calculateProgressToNextLevel(
    completionRatio: number,
    averageQuizScore: number,
    currentLevel: number,
  ): number {
    const levelThresholds = [0, 0.2, 0.4, 0.6, 0.8, 1.0];
    const currentThreshold = levelThresholds[currentLevel - 1];
    const nextThreshold = levelThresholds[currentLevel];

    if (currentLevel === 5) return 100;

    const levelProgress =
      (completionRatio - currentThreshold) / (nextThreshold - currentThreshold);
    const quizBonus = Math.max(0, (averageQuizScore - 70) / 30) * 0.2;

    return Math.min(100, Math.round((levelProgress + quizBonus) * 100));
  }

  private analyzeSkillPerformance(
    topicProgress: any[],
    quizScores: number[],
    relatedTopics: any[],
  ): { strengths: string[]; weaknesses: string[] } {
    const strengths: string[] = [];
    const weaknesses: string[] = [];

    // Analyze topic completion
    const completedTopics = topicProgress.filter((tp) => tp.progress >= 100);
    const incompleteTopics = topicProgress.filter((tp) => tp.progress < 50);

    if (completedTopics.length > relatedTopics.length * 0.7) {
      strengths.push("Strong topic completion");
    } else if (incompleteTopics.length > relatedTopics.length * 0.5) {
      weaknesses.push("Many topics incomplete");
    }

    // Analyze quiz performance
    const averageQuizScore =
      quizScores.length > 0
        ? quizScores.reduce((sum, score) => sum + score, 0) / quizScores.length
        : 0;

    if (averageQuizScore >= 85) {
      strengths.push("Excellent quiz performance");
    } else if (averageQuizScore < 70) {
      weaknesses.push("Quiz scores need improvement");
    }

    // Analyze difficulty progression
    const advancedTopics = relatedTopics.filter(
      (topic) => topic.difficulty === "advanced",
    );
    const completedAdvanced = advancedTopics.filter(
      (topic) =>
        topicProgress.find((tp) => tp.id === topic.id)?.progress >= 100,
    );

    if (completedAdvanced.length > 0) {
      strengths.push("Advanced topic mastery");
    } else if (advancedTopics.length > 0) {
      weaknesses.push("Advanced topics not attempted");
    }

    return { strengths, weaknesses };
  }

  private calculateSkillTrend(
    educationData: EducationData,
    skillId: string,
  ): "improving" | "stable" | "declining" {
    // Simple trend calculation based on recent activity
    const recentProgress = Object.values(educationData.progress).filter(
      (progress) => {
        const lastAccessed = new Date(progress.lastAccessed);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return lastAccessed > weekAgo;
      },
    );

    if (recentProgress.length === 0) return "stable";

    const averageRecentScore = recentProgress
      .flatMap((progress) => progress.quizScores || [])
      .reduce((sum, score, _, arr) => sum + score / arr.length, 0);

    if (averageRecentScore >= 85) return "improving";
    if (averageRecentScore < 70) return "declining";
    return "stable";
  }

  private getNextMilestone(
    currentLevel: number,
  ): SkillMetrics["nextMilestone"] {
    const nextLevel = Math.min(5, currentLevel + 1);
    const criteria =
      this.skillLevelCriteria[
        nextLevel as keyof typeof this.skillLevelCriteria
      ];

    return {
      level: nextLevel,
      requirements: criteria.requirements,
      estimatedTime: nextLevel === currentLevel + 1 ? "2-4 weeks" : "4-8 weeks",
    };
  }

  private generateSkillAssessments(
    skillId: string,
    educationData: EducationData,
  ): SkillAssessment[] {
    // Generate mock assessments based on progress data
    const assessments: SkillAssessment[] = [];

    Object.entries(educationData.progress).forEach(([lessonId, progress]) => {
      if (progress.quizScores && progress.quizScores.length > 0) {
        const latestScore = Math.max(...progress.quizScores);
        assessments.push({
          id: `${skillId}-${lessonId}`,
          skillId,
          skillName: this.formatSkillName(skillId),
          level: Math.ceil(latestScore / 20) as 1 | 2 | 3 | 4 | 5,
          assessedAt: progress.lastAccessed,
          method: "quiz",
          score: latestScore,
          expiresAt: undefined,
        });
      }
    });

    return assessments;
  }

  private calculateOverview(
    educationData: EducationData,
    curriculumService: any,
  ): ProgressAnalytics["overview"] {
    const totalLessons = Object.keys(educationData.progress).length;
    const completedLessons = Object.values(educationData.progress).filter(
      (p) => p.completed,
    ).length;
    const totalTimeSpent = Object.values(educationData.progress).reduce(
      (sum, p) => sum + p.timeSpent,
      0,
    );
    const averageQuizScore = this.calculateAverageQuizScore(educationData);

    return {
      totalLessons,
      completedLessons,
      totalTopics: curriculumService.getAllTopics().length,
      completedTopics: curriculumService
        .getAllTopics()
        .filter((topic: any) =>
          curriculumService.isTopicCompleted(topic.id, educationData.progress),
        ).length,
      totalPaths: curriculumService.getAllLearningPaths().length,
      completedPaths: curriculumService
        .getAllLearningPaths()
        .filter((path: any) =>
          curriculumService.isLearningPathCompleted(
            path.id,
            educationData.progress,
          ),
        ).length,
      overallProgress:
        totalLessons > 0
          ? Math.round((completedLessons / totalLessons) * 100)
          : 0,
      averageQuizScore,
      totalTimeSpent,
      streakDays: this.calculateStreakDays(educationData),
      lastActivityDate: this.getLastActivityDate(educationData),
    };
  }

  private getAllSkillMetrics(
    educationData: EducationData,
    curriculumService: any,
  ): SkillMetrics[] {
    const allSkills = new Set<string>();
    curriculumService.getAllTopics().forEach((topic: any) => {
      topic.skills.forEach((skill: string) => allSkills.add(skill));
    });

    return Array.from(allSkills).map((skillId) =>
      this.assessSkillLevel(skillId, educationData, curriculumService),
    );
  }

  private generateLearningInsights(
    educationData: EducationData,
    curriculumService: any,
    skills: SkillMetrics[],
  ): LearningInsight[] {
    return this.generateLearningRecommendations(
      educationData,
      curriculumService,
    );
  }

  private calculateTrends(
    educationData: EducationData,
  ): ProgressAnalytics["trends"] {
    return {
      weeklyProgress: this.calculateWeeklyProgress(educationData),
      skillProgression: [],
    };
  }

  private generateRecommendations(
    educationData: EducationData,
    curriculumService: any,
    skills: SkillMetrics[],
  ): ProgressAnalytics["recommendations"] {
    const weakSkills = skills
      .filter((skill) => skill.progress < 50)
      .map((s) => s.skillId);
    const nextTopics = curriculumService
      .getRecommendedNextTopics(
        Object.keys(educationData.progress).filter(
          (id) => educationData.progress[id]?.completed,
        ),
        educationData.progress,
      )
      .map((topic: any) => topic.id);

    return {
      nextTopics,
      recommendedPaths: [],
      skillFocus: weakSkills,
      timeAllocation: weakSkills.reduce(
        (acc, skill) => ({ ...acc, [skill]: 30 }),
        {},
      ),
    };
  }

  private calculateAverageQuizScore(educationData: EducationData): number {
    const allScores = Object.values(educationData.progress)
      .flatMap((progress) => progress.quizScores || [])
      .filter((score) => score > 0);

    return allScores.length > 0
      ? Math.round(
          allScores.reduce((sum, score) => sum + score, 0) / allScores.length,
        )
      : 0;
  }

  private calculateStreakDays(educationData: EducationData): number {
    // Simple streak calculation - could be enhanced
    const recentActivity = Object.values(educationData.progress)
      .map((progress) => new Date(progress.lastAccessed))
      .sort((a, b) => b.getTime() - a.getTime());

    if (recentActivity.length === 0) return 0;

    let streak = 1;
    const today = new Date();

    for (let i = 0; i < recentActivity.length - 1; i++) {
      const current = recentActivity[i];
      const previous = recentActivity[i + 1];
      const daysDiff = Math.floor(
        (current.getTime() - previous.getTime()) / (1000 * 60 * 60 * 24),
      );

      if (daysDiff <= 1) {
        streak++;
      } else {
        break;
      }
    }

    return streak;
  }

  private getLastActivityDate(educationData: EducationData): string {
    const dates = Object.values(educationData.progress).map(
      (progress) => progress.lastAccessed,
    );
    return dates.length > 0
      ? new Date(
          Math.max(...dates.map((d) => new Date(d).getTime())),
        ).toISOString()
      : new Date().toISOString();
  }

  private calculateWeeklyProgress(
    educationData: EducationData,
  ): ProgressAnalytics["trends"]["weeklyProgress"] {
    // Generate mock weekly data for now
    const weeks = [];
    for (let i = 7; i >= 0; i--) {
      const week = new Date();
      week.setDate(week.getDate() - i * 7);
      weeks.push({
        week: week.toISOString().split("T")[0],
        lessonsCompleted: Math.floor(Math.random() * 5),
        timeSpent: Math.floor(Math.random() * 300),
        quizScore: 70 + Math.floor(Math.random() * 25),
      });
    }
    return weeks;
  }

  private calculateConsistencyScore(educationData: EducationData): number {
    const scores = Object.values(educationData.progress)
      .flatMap((progress) => progress.quizScores || [])
      .filter((score) => score > 0);

    if (scores.length < 2) return 50;

    const average =
      scores.reduce((sum, score) => sum + score, 0) / scores.length;
    const variance =
      scores.reduce((sum, score) => sum + Math.pow(score - average, 2), 0) /
      scores.length;
    const standardDeviation = Math.sqrt(variance);

    // Lower standard deviation = higher consistency
    return Math.max(0, Math.min(100, 100 - standardDeviation * 2));
  }

  private calculateBreadthScore(
    skillId: string,
    educationData: EducationData,
    curriculumService: any,
  ): number {
    const relatedTopics = curriculumService
      .getAllTopics()
      .filter((topic: any) => topic.skills.includes(skillId));

    if (relatedTopics.length === 0) return 0;

    const completedTopics = relatedTopics.filter((topic: any) =>
      curriculumService.isTopicCompleted(topic.id, educationData.progress),
    );

    return Math.round((completedTopics.length / relatedTopics.length) * 100);
  }
}

// Export singleton instance
export const skillAssessmentService = new SkillAssessmentService();
